/**
 * Tenant subdomain detection for multi-tenant subdomain login.
 *
 * Backend resolves tenant_id from the Host header when tenant_code is omitted
 * (see nue2025_back/api/v1/auth.py:get_tenant_from_subdomain_or_request).
 * The frontend uses this helper to:
 *   1. Decide whether to hide the org-code input on /login.
 *   2. Show "Signing in to <subdomain>" affordance.
 *
 * Heuristic — a hostname is treated as a tenant subdomain when:
 *   - it has 3+ dotted parts (foo.bar.tld), AND
 *   - the first label is not in the reserved list (www, api, app, admin, mail, …).
 *
 * Bare apex domains, IPs, and localhost return null → form falls back to manual entry.
 */

const RESERVED_SUBDOMAINS = new Set([
  'www', 'api', 'app', 'admin', 'mail', 'static', 'cdn', 'assets',
  'docs', 'help', 'support', 'status', 'blog', 'marketing',
]);

const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/;

export interface TenantHost {
  subdomain: string;       // e.g. "acme"
  rootDomain: string;      // e.g. "dataver.co"
  fullHost: string;        // e.g. "acme.dataver.co"
}

export function detectTenantSubdomain(hostname?: string): TenantHost | null {
  const raw = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase().trim();
  if (!raw) return null;

  // localhost / IPs → no subdomain
  if (raw === 'localhost' || raw.endsWith('.localhost')) return null;
  if (IPV4_RE.test(raw)) return null;

  const parts = raw.split('.');
  if (parts.length < 3) return null;          // bare apex like "dataver.co"

  const subdomain = parts[0];
  if (RESERVED_SUBDOMAINS.has(subdomain)) return null;

  return {
    subdomain,
    rootDomain: parts.slice(1).join('.'),
    fullHost: raw,
  };
}
